
    import __i18nConfig from '@next-translate-root/i18n.js'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import Image from "next/image";
import useTranslation from "next-translate/useTranslation";
import { MDXRemoteSerializeResult } from "next-mdx-remote";
import { Prose } from "@components/strapi/implementation/shared-ui";
/**
 * The body of a treatment, condition or team-member page — the entity the
 * union route resolved (D35). The web-page instance behind it supplies the
 * shell (related links, sidebar, CTA); this renders the entity itself.
 *
 * Treatments/conditions: h1, teaser, cover, the long Q&A body, and — for
 * conditions — the symptom list in its own box, because a searcher
 * recognising their own symptoms is the page's first job.
 *
 * Team members: the clinician's CV — portrait beside name + credentials, then
 * the bio. The portrait sits NEXT to the heading rather than above it: a CV
 * page is read (who is this, what are they certified in), not browsed, so the
 * text must start above the fold even with a tall portrait.
 */
export interface EntityDetailProps {
    entity: {
        // treatments / conditions
        title?: string;
        teaser?: string;
        description?: MDXRemoteSerializeResult;
        symptoms?: MDXRemoteSerializeResult;
        coverImage?: {
            url: string;
            alternativeText?: string;
            width?: number;
            height?: number;
        };
        // team members
        displayName?: string;
        qualification?: string;
        role?: string;
        bio?: MDXRemoteSerializeResult;
        photo?: {
            url: string;
            alternativeText?: string;
        };
    };
    kind: "treatment" | "condition" | "team";
}
export default function EntityDetail({ entity, kind }: EntityDetailProps) {
    const { t } = useTranslation("main");
    if (kind === "team") {
        return (<article>
        <header className="flex flex-col gap-6 sm:flex-row sm:items-center">
          {/* w-64/w-56 matches the related-member cards below (256px mobile,
                224px desktop) — the page's own subject must not render smaller
                than the "see also" faces (owner, 2026-09-14) */}
          <div className={`blob-1 relative aspect-square w-64 flex-none overflow-hidden sm:w-56 ${entity.photo ? "bg-violet-400" : "bg-violet-050"}`}>
            {entity.photo ? (<Image src={entity.photo.url} alt={entity.displayName ?? ""} fill sizes="(min-resolution: 2dppx) 12.1rem, (min-width:640px) 14rem, 16rem" quality={90} className="portrait-photo" priority fetchPriority="high"/>) : (
            // D49: never a stand-in face — the brand symbol until the shoot lands
            <div className="absolute inset-0 grid place-items-center">
                <Image src="/images/brand/symbol-color.svg" alt="" width={56} height={79}/>
              </div>)}
          </div>
          {/* min-w-0: lets the name column shrink in the sm+ flex row —
                «Παπακωνσταντοπούλου» is one heading-sized word (globals.css
                hyphenates it when it can't fit the line) */}
          <div className="min-w-0">
            <h1 className="text-3xl font-extrabold tracking-tight text-ink md:text-4xl">{entity.displayName}</h1>
            {(entity.qualification || entity.role) ? (<p className="mt-2 text-lg text-muted">
                {[entity.qualification, entity.role].filter(Boolean).join(" · ")}
              </p>) : null}
          </div>
        </header>

        <Prose content={entity.bio} className="mt-8"/>
      </article>);
    }
    return (<article>
      <header>
        <h1 className="text-3xl font-extrabold tracking-tight text-ink md:text-4xl">{entity.title}</h1>
        {/* the teaser is a lede — serif, like the hero's (owner, 2026-09-09) */}
        {entity.teaser ? <p className="mt-3 max-w-2xl font-serif text-lg text-muted">{entity.teaser}</p> : null}
      </header>

      {entity.coverImage ? (
        // Portrait sources get a taller frame and a CENTERED crop — the global
        // .clinical-photo 50%/42% band is tuned for landscape shots and was
        // cropping the subject out of portrait photos (owner, 2026-09-14:
        // ρεύματα/μάλαξη/χειροθεραπεία). The blob mask hides the ratio change.
        <div className={`blob-2 relative mt-6 max-w-2xl overflow-hidden bg-violet-050 ${(entity.coverImage.height ?? 0) > (entity.coverImage.width ?? 0) ? "aspect-[4/3]" : "aspect-[16/9]"}`}>
          <Image src={entity.coverImage.url} alt={entity.coverImage.alternativeText ?? entity.title ?? ""} fill sizes="(min-width:1024px) 42rem, 100vw" className="clinical-photo" priority 
        // the cover IS the LCP on entity pages — high wins the bandwidth
        // race for the preload too (LCP-discovery insight, 2026-09-16)
        fetchPriority="high" style={(entity.coverImage.height ?? 0) > (entity.coverImage.width ?? 0)
                ? { objectPosition: "50% 50%" } : undefined}/>
        </div>) : null}

      {kind === "condition" && entity.symptoms ? (<aside className="mt-8 max-w-2xl rounded-2xl border border-violet-200 bg-violet-050 p-6">
          <h2 className="text-lg font-extrabold text-ink">{t("entity.symptomsHeading")}</h2>
          {/* no text-sm: the override trapped this READING list at 14px
                serif once prose went Source Serif (owner, 2026-09-09) */}
          <Prose content={entity.symptoms} className="mt-2 [&_ul]:mb-0"/>
        </aside>) : null}

      <Prose content={entity.description} className="mt-8"/>
    </article>);
}

    async function __Next_Translate__getStaticProps__1a0b0b8f75f__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      
      return {
        
        
        props: {
          
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: 'components/layout//EntityDetail',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || (() => Promise.resolve({})),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a0b0b8f75f__ as getStaticProps }
  